/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, IconButton, makeStyles } from '@material-ui/core';
import InputIcon from '@material-ui/icons/Input';
import clsx from 'clsx';
import RefreshButton from 'components/RefreshButton';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { getInstancesByUsageRequest } from 'modules/partners/actions';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import {
  InstancesByUsageFields,
  InstancesByUsageProps,
  PartnerFormFields,
} from 'modules/partners/interfaces';
import { getFlavorDisplayName } from 'utils/flavorDisplay';
import { cyrb53Hash, formatHourMinuteSecond } from 'utils/helpers';
import { isClientOperator } from 'utils/role';
import PercentIndicator from 'components/PercentIndicator';
import styles from './styles';
import PartnersTemplate from '../PartnersTemplate';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface Props {
  isLoading: boolean;
  instancesByUsage: InstancesByUsageProps;
  companySelected: CompanyData | null;
  permissions?: PermissionsData;
  partner: PartnerFormFields | null;
  role?: RolesEnum;
  getInstancesByUsageRequest: (props) => void;
}

const ListInstanceByUsage = ({
  isLoading,
  instancesByUsage,
  companySelected,
  permissions,
  partner,
  role,
  getInstancesByUsageRequest,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const history = useHistory();
  const [refresh, setRefresh] = React.useState(false);
  const { id } = useParams<{ id: string }>();

  const headers = [
    {
      key: 'environment_name',
      i18n: 'ENVIRONMENT_KEY',
      order: true,
    },
    {
      key: 'instance_name',
      i18n: 'INSTANCE_KEY',
      order: true,
    },
    {
      key: 'flavor',
      i18n: 'TYPE_KEY',
      order: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'current_cpu_percent',
      i18n: 'CPU_CURRENT_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'current_memory_percent',
      i18n: 'MEMORY_CURRENT_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'current_disk_c_percent',
      i18n: 'DISK_C_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'current_sessions',
      i18n: 'SESSIONS_CURRENT_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'collection_time',
      i18n: 'COLLECTION_TIME_KEY',
      order: true,
      centerTitle: true,
    },
  ].filter(item => !item?.hidden);

  const getInstances = (refresh = false) => {
    getInstancesByUsageRequest({
      query,
      id,
    });

    setRefresh(refresh);
  };

  React.useEffect(() => {
    getInstances();
  }, [getInstancesByUsageRequest, query, companySelected]);

  React.useEffect(() => {
    const POLLING_MS = 60_000;

    const tick = () => {
      if (document.hidden) return;
      if (isLoading) return;

      getInstances(true);
    };

    const intervalId = window.setInterval(tick, POLLING_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [id, query.offset, query.limit, query.search, isLoading]);

  const handleClick = (item: InstancesByUsageFields) => {
    if (permissions?.list_environment_instances) {
      history.push(
        `/environments/${item?.environment_uuid}/instances/${item?.instance_name}`
      );
    }
  };

  return (
    <PartnersTemplate
      current="instances"
      title={i18n.t('PERFORMANCE_ANALYSIS_TITLE_KEY')}
      subtitle={i18n.t('PERFORMANCE_ANALYSIS_SUBTITLE_KEY')}
      partnerName={partner?.name}
      breadcrumbs={[
        {
          name: i18n.t('PARTNERS_KEY'),
          link: '/instances',
        },
        {
          name: i18n.t('INSTANCES_KEY'),
        },
      ]}
      button={
        <RefreshButton
          id="btn-refresh"
          onClick={() => getInstances(true)}
          animate={isLoading}
          disabled={isLoading}
        />
      }
    >
      <Grid container>
        <Grid container>
          <SearchBar
            id="search-instances"
            isLoading={isLoading}
            searchValue={searchValue}
            placeholder={i18n.t('SEARCH_KEY')}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
          <TableItems
            header={
              <>
                <Permission value="list_environment_instances">
                  <TableCell className={classes.accessButtonHeader} centerTitle>
                    {i18n.t('ACCESS_KEY')}
                  </TableCell>
                </Permission>
                {headers.map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    key={item?.key}
                    order={item?.order}
                    markOderItem={itemOrder === item?.key}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: InstancesByUsageFields, index) => (
              <TableRow
                className={clsx(classes.summaryRowTable, {
                  [classes.envRowDirect]:
                    !!permissions && permissions.list_environment_instances,
                })}
                key={cyrb53Hash(index)}
              >
                <Permission value="list_environment_instances">
                  <TableCell
                    ellipsis
                    centerTitle
                    className={classes.centerFields}
                    onClick={() => {
                      handleClick(item);
                    }}
                  >
                    <IconButton>
                      <InputIcon className={classes.accessButton} />
                    </IconButton>
                  </TableCell>
                </Permission>
                <TableCell
                  copyable
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textField}
                >
                  {item?.environment_name}
                </TableCell>
                <TableCell
                  copyable
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textField}
                >
                  {item?.instance_name}
                </TableCell>
                {!isClientOperator(role) && (
                  <TableCell
                    onClick={() => {
                      handleClick(item);
                    }}
                    className={classes.textField}
                  >
                    {getFlavorDisplayName(item?.flavor)}
                  </TableCell>
                )}
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textFieldCenter}
                >
                  <PercentIndicator value={item?.current_cpu_percent} />
                </TableCell>

                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textFieldCenter}
                >
                  <PercentIndicator value={item?.current_memory_percent} />
                </TableCell>

                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textFieldCenter}
                >
                  <PercentIndicator value={item?.current_disk_c_percent} />
                </TableCell>

                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textFieldCenter}
                >
                  {item?.current_sessions}
                </TableCell>
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={classes.textFieldCenter}
                >
                  <>{formatHourMinuteSecond(item?.collection_time)}</>
                </TableCell>
              </TableRow>
            )}
            data={instancesByUsage}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('LOGS_PAGE.NO_LOGS_KEY')}
          />
        </Grid>
      </Grid>
    </PartnersTemplate>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  partners: { instancesByUsage, isLoadingInstancesByUsage, partnerDetails },
}: ApplicationState) => ({
  companySelected,
  permissions: data?.permissions,
  partner: partnerDetails,
  instancesByUsage,
  role: data?.role,
  isLoading: isLoadingInstancesByUsage,
});

export default connect(mapStateToProps, {
  getInstancesByUsageRequest,
})(ListInstanceByUsage);
